import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useParams, useLocation, matchPath } from 'react-router-dom';
import ProductRoute from './components/ProductRoute';
import { AuthProvider } from './context/AuthContext';
import { FunnelProvider } from './context/FunnelContext';
import NotFound from './pages/404';
import Home from './pages/Home';
import Login from './pages/Login';
import FullPageLoader from './components/FullPageLoader';
import MicrosoftClarity from './tracking/MicrosoftClarity';
import MetaPixel from './tracking/MetaPixel';
import HeapTracking from './tracking/HeapTracking';
import GoogleAnalytics from './tracking/GoogleAnalytics';
import TikTokPixel from './tracking/TikTokPixel';
import FunnelRoute from './components/FunnelRoute';
import ProtectedRoute from './context/ProtectedRoute';
import DynamicUpsellHandler from './components/DynamicUpsell';
import { getBrand } from './utils/getBrand';
import { isMarketingPath } from './utils/marketingRedirect';
import MomApplyHome from './pages/MomApplyHome';
import RemoteApplyHome from './pages/RemoteApplyHome';
import { useCookieYes } from './hooks/useCookieYes';
import AccessibilityWidget from './components/AccessibilityWidget';
import { initA11yPrefs } from './utils/a11yPrefs';
import {
  getInternalTrackingApiKey,
  getInternalTrackingEndpoint,
  isInternalTrackingEnabled,
} from './utils/internalTracking';
import { TrackingProvider } from '@better-apply/tracking-package/react';

// Product and secondary pages are code-split so the marketing/funnel entry
// points don't pay for the whole product bundle
const Onboarding = lazy(() => import('@pages/product/Onboarding'));
const AutoApply = lazy(() => import('@pages/product/AutoApply'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/product/Dashboard'));
const EditResume = lazy(() => import('./pages/product/EditResume'));
const MatchedJobs = lazy(() => import('./pages/product/MatchedJobs'));
const SavedJobs = lazy(() => import('./pages/product/SavedJobs'));
const OptimizeResumePage = lazy(() => import('./pages/product/OptimizeResumePage'));
const DocumentsPreviewPage = lazy(() => import('./pages/product/DocumentsPreviewPage'));
const Terms = lazy(() => import('./pages/Terms'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Profile = lazy(() => import('./pages/product/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const HomePage = lazy(() => import('./pages/HomePage'));
const Inbox = lazy(() => import('./pages/product/Inbox'));
const OptimizeCoverLetter = lazy(() => import('./pages/product/OptimizeCoverLetter'));
const SubscriptionExpired = lazy(() => import('./pages/product/SubscriptionExpired'));
const Job = lazy(() => import('./pages/product/Job'));
const BrowseJobs = lazy(() => import('./pages/product/BrowseJobs'));
const LinkedInImage = lazy(() => import('./pages/product/LinkedInImage'));
const JobBrowse = lazy(() => import('./pages/product/JobBrowse'));

function InternalTrackingProvider({ eventCategory, globalProperties }) {
  return (
    <TrackingProvider
      enabled={isInternalTrackingEnabled()}
      endpoint={getInternalTrackingEndpoint()}
      apiKey={getInternalTrackingApiKey()}
      env={process.env.REACT_APP_ENV}
      event_category={eventCategory}
      globalProperties={globalProperties}
      debug={process.env.REACT_APP_ENV !== 'production'}
    />
  );
}

function FunnelProviderWrapper() {
  const { funnelId } = useParams();
  const { brandName } = getBrand();

  return (
    <FunnelProvider funnelId={funnelId}>
      <InternalTrackingProvider
        eventCategory="funnel"
        globalProperties={{ funnelId, projectName: brandName }}
      />
      <Outlet />
    </FunnelProvider>
  );
}

// Layout for product routes, provides Auth context
function ProductProviderWrapper() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const BRAND_HOMEPAGES = {
  MomApply: MomApplyHome,
  RemoteApply: RemoteApplyHome,
};

function DomainHomeRoute() {
  const { brandName, brandCookieYesId } = getBrand();
  useCookieYes(brandCookieYesId);
  const BrandHome = BRAND_HOMEPAGES[brandName] || Home;

  return (
    <>
      <InternalTrackingProvider
        eventCategory="funnel"
        globalProperties={{ projectName: brandName }}
      />
      <BrandHome />
    </>
  );
}

const PAGE_TITLES = [
  ['/login', 'Sign in'],
  ['/reset-password', 'Reset password'],
  ['/dashboard', 'Dashboard'],
  ['/matched-jobs', 'Matched jobs'],
  ['/saved-jobs', 'Saved jobs'],
  ['/browse-jobs', 'Browse jobs'],
  ['/jobs-tracker', 'Jobs tracker'],
  ['/job/:matchId', 'Job details'],
  ['/job-browse/:id', 'Job details'],
  ['/optimize-resume/:id', 'Optimize resume'],
  ['/optimize-cover-letter/:id', 'Optimize cover letter'],
  ['/edit-resume/:id', 'Edit resume'],
  ['/documents-preview/:id', 'Document preview'],
  ['/profile', 'Profile'],
  ['/linkedin-image', 'LinkedIn photo'],
  ['/auto-apply', 'Auto apply'],
  ['/inbox', 'Inbox'],
  ['/onboarding', 'Getting started'],
  ['/subscription-expired', 'Subscription expired'],
  ['/terms-and-conditions', 'Terms and conditions'],
  ['/contact-us', 'Contact us'],
  ['/about-us', 'About us'],
  ['/privacy-policy', 'Privacy policy'],
  ['/:funnelId/quiz/:questionNr', ({ questionNr }) => `Question ${questionNr}`],
  ['/:funnelId/email', 'Your results'],
  ['/:funnelId/summary/:code', 'Your results'],
  ['/:funnelId/checkout/:code?', 'Choose your plan'],
  ['/:funnelId/payment/:code', 'Payment'],
  ['/:funnelId/offer/quiz/:questionNr/:code', ({ questionNr }) => `Question ${questionNr}`],
  ['/:funnelId/offer/success/:code', 'Welcome aboard'],
];

function usePageTitle(pathname) {
  useEffect(() => {
    const { brandName } = getBrand();
    const baseTitle = `${brandName} - Automate Your Job Hunt`;
    let title = baseTitle;
    for (const [pattern, label] of PAGE_TITLES) {
      const match = matchPath(pattern, pathname);
      if (match) {
        const resolved = typeof label === 'function' ? label(match.params) : label;
        title = `${resolved} — ${brandName}`;
        break;
      }
    }
    document.title = title;
  }, [pathname]);
}

function AppContent() {
  const location = useLocation();
  const funnelMatch = matchPath('/:funnelId/*', location.pathname);
  const funnelId = funnelMatch?.params?.funnelId;
  usePageTitle(location.pathname);

  const { brandCookieYesId, brandCookieYesAllPages, brandMarketingDisabled } = getBrand();
  useCookieYes(brandCookieYesAllPages ? brandCookieYesId : undefined);

  // Apply saved accessibility preferences (contrast, text size) on every
  // page load — the widget button itself only renders on select routes.
  useEffect(() => {
    initA11yPrefs();
  }, []);

  // Brands with acquisition switched off (PayLeap) never render the homepage
  // or the free funnel steps — those go straight to sign-in. Query string is
  // kept so ad/UTM params still reach the login page.
  if (brandMarketingDisabled && isMarketingPath(location.pathname)) {
    return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
  }

  return (
    <>
      <AccessibilityWidget />
      <MetaPixel funnelId={funnelId} />
      <HeapTracking />
      <GoogleAnalytics />
      <TikTokPixel />
      <MicrosoftClarity />

      <Suspense fallback={<FullPageLoader />}>
      <Routes>
        {/* Dynamic funnel routes */}
        <Route path="/:funnelId" element={<FunnelProviderWrapper />}>
          <Route path="landing" element={<FunnelRoute componentName="Landing" />} />
          <Route path="offer" element={<Outlet />}>
            <Route path=":upsell/:code" element={<DynamicUpsellHandler />} />
          </Route>
          <Route path="quiz/:questionNr" element={<FunnelRoute componentName="Quiz" />} />
          <Route path="email" element={<FunnelRoute componentName="Email" />} />
          <Route path="summary/:code" element={<FunnelRoute componentName="Summary" />} />
          <Route path="checkout/:code?" element={<FunnelRoute componentName="Checkout" />} />
          <Route path="payment/:code" element={<FunnelRoute componentName="Payment" />} />
          {/* Success with auth */}
          <Route element={<ProductProviderWrapper />}>
            <Route path="quiz/:questionNr/:code" element={<FunnelRoute componentName="Quiz" />} />
            <Route path="success/:code" element={<FunnelRoute componentName="Success" />} />
          </Route>
        </Route>

        <Route path="/404" element={<NotFound />} />
        <Route path="/" element={<DomainHomeRoute />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="terms-and-conditions" element={<Terms />} />
        <Route path="contact-us" element={<ContactUs />} />
        <Route path="about-us" element={<AboutUs />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />

        {/* Product routes (not wrapped in FunnelProvider) */}
        <Route element={<ProductProviderWrapper />}>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* All routes under this are protected */}
          <Route element={<ProtectedRoute />}>
            <Route element={<ProductRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/matched-jobs" element={<MatchedJobs />} />
              <Route path="/saved-jobs" element={<SavedJobs />} />
              <Route path="/browse-jobs" element={<BrowseJobs />} />
              {/* <Route path="/documents" element={<Document />} /> */}
              <Route path="/jobs-tracker" element={<MatchedJobs />} />
              <Route path="/job/:matchId" element={<Job />} />
              <Route path="/job-browse/:id" element={<JobBrowse />} />
              <Route path="/optimize-resume/:id" element={<OptimizeResumePage />} />
              <Route path="/optimize-cover-letter/:id" element={<OptimizeCoverLetter />} />
              <Route path="/edit-resume/:id" element={<EditResume />} />
              <Route path="/documents-preview/:id" element={<DocumentsPreviewPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/linkedin-image" element={<LinkedInImage />} />
              <Route path="/auto-apply" element={<AutoApply />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/subscription-expired" element={<SubscriptionExpired />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

// Main App - NO HOOKS, just wraps everything in Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
